import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Avatar,
  Button,
  Grid,
  MenuItem,
  Popover,
  Select,
  useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';
import PopupTitle from '../common/popup_title';
import {
  DAY_INDEX_SHORT_LABELS,
  dayIndexesWithStartDay,
} from '../../services/planner';
import WeekNavigator from '../common/week_navigator';
import PlanNavigator from '../programmes/plan_navigator';
import { useGetProgrammesQuery } from '../../app/smorgAppSyncGraphQLApi';
import { programmesComparer } from '../../services/programmes';
import { switchToViewWeek } from '../../features/end_user_planner/end_user_planner_slice';
import {
  addToPlannerDestinationChanged,
  addToPlannerDestinationPlanIdChanged,
} from '../../features/smorg_studio_programmes/smorg_studio_programmes_slice';
import StepInput from '../common/step_input';
import {
  aggregateServings,
  persistScaleMealsToServingsByKindOfPerson,
} from '../../features/end_user_collections/end_user_collections_slice';

const PLANNER_DESTINATION = 'Planner';

const AddToPlannerModal = ({
  anchorEl,
  copyToPlannerEnabled,
  copyToProgrammesEnabled,
  weekDaySelectionDisabled,
  onCopyToPlanner,
  onCopyToProgramme,
  destinationProgrammeIDs,
  onDismiss,
  titleText,
  editServingsEnabled,
}) => {
  const dispatch = useDispatch();

  const userPlannerViewStartDay = 1; /* plannerViewWeekStartDayOrDefault(
    userProfile?.plannerViewWeekStartDay,
  ) */

  const lastUsedViewStartDate = useSelector(
    (state) => state.endUserPlanner.currentPlannerViewWeekStartDate,
  );

  const { data: ownedProgrammesUnsorted } = useGetProgrammesQuery(undefined);

  const ownedProgrammes = useMemo(() => {
    if (!ownedProgrammesUnsorted) {
      return [];
    }
    const ownedProgrammesSorted = [...ownedProgrammesUnsorted];
    ownedProgrammesSorted.sort(programmesComparer);
    return ownedProgrammesSorted;
  }, [ownedProgrammesUnsorted]);

  const programmes =
    destinationProgrammeIDs.length > 0
      ? ownedProgrammes.filter((p) => destinationProgrammeIDs.includes(p.id))
      : ownedProgrammes;

  const [plannerViewWeekStartDate, setPlannerViewWeekStartDate] = useState(
    lastUsedViewStartDate,
  );

  const [dayIndexes, setDayIndexes] = useState([]);

  const destination = useSelector(
    (state) => state.smorgStudioProgrammes.currentAddToPlannerDestination,
  );
  const destinationPlanId = useSelector(
    (state) => state.smorgStudioProgrammes.currentAddToPlannerDestinationPlanId,
  );

  useEffect(() => {
    if (destination === PLANNER_DESTINATION && !copyToPlannerEnabled) {
      dispatch(addToPlannerDestinationChanged(programmes[0]?.id));
      dispatch(
        addToPlannerDestinationPlanIdChanged(
          (programmes[0]?.plans || [])[0]?.id,
        ),
      );
    } else if (
      destination !== PLANNER_DESTINATION &&
      !copyToProgrammesEnabled
    ) {
      dispatch(addToPlannerDestinationChanged(PLANNER_DESTINATION));
    } else if (
      destination !== PLANNER_DESTINATION &&
      !programmes.some((p) => p.id === destination)
    ) {
      dispatch(addToPlannerDestinationChanged(programmes[0]?.id));
      dispatch(
        addToPlannerDestinationPlanIdChanged(
          (programmes[0]?.plans || [])[0]?.id,
        ),
      );
    }
  }, [
    copyToPlannerEnabled,
    copyToProgrammesEnabled,
    destination,
    dispatch,
    programmes,
  ]);

  const clearState = () => {
    setDayIndexes([]);
  };

  const onClose = () => {
    clearState();
    onDismiss();
  };

  const onExcludeDayIndex = (dayIndex) => {
    setDayIndexes(dayIndexes.filter((idx) => idx !== dayIndex));
  };

  const onIncludeDayIndex = (dayIndex) => {
    if (dayIndexes.includes(dayIndex)) {
      return;
    }
    setDayIndexes([...dayIndexes, dayIndex]);
  };

  const theme = useTheme();

  const willCopyToPlanner = destination === PLANNER_DESTINATION;

  const destinationProgramme =
    programmes.find((pr) => pr.id === destination) || null;

  const allDayIndexes = willCopyToPlanner
    ? dayIndexesWithStartDay(userPlannerViewStartDay)
    : dayIndexesWithStartDay(1);

  const onChangeDestination = (newDestination) => {
    dispatch(addToPlannerDestinationChanged(newDestination));
    if (newDestination !== PLANNER_DESTINATION) {
      const programme = programmes.find((pr) => pr.id === newDestination);
      dispatch(addToPlannerDestinationPlanIdChanged(programme.plans[0].id));
    }
  };

  const onChangeDestinationPlanId = (newDestinationPlanId) => {
    dispatch(addToPlannerDestinationPlanIdChanged(newDestinationPlanId));
  };

  const scaleMealsTo = useSelector(
    (state) => state.endUserCollections.scaleMealsTo,
  );

  const [scaleMealsToStrs, setScaleMealsToStrs] = useState({
    adults: '',
    children: '',
  });

  useEffect(() => {
    const updatedScaleMealsToStrs = { ...scaleMealsToStrs };
    Object.keys(scaleMealsTo).forEach((key) => {
      updatedScaleMealsToStrs[key] = String(scaleMealsTo[key]);
    });
    setScaleMealsToStrs(updatedScaleMealsToStrs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(scaleMealsTo)]);

  const onChangeScaleMealsTo = (field, value) => {
    setScaleMealsToStrs({
      ...scaleMealsToStrs,
      [field]: value,
    });
    const newValue = Number(value);
    if (Number.isNaN(newValue) || newValue < 1) {
      return;
    }
    const updatedScaleMealsTo = { ...scaleMealsTo, [field]: newValue };
    dispatch(persistScaleMealsToServingsByKindOfPerson(updatedScaleMealsTo));
  };

  return (
    <Popover
      open
      PaperProps={{
        style: { width: '320px', height: '305px', padding: '10px' },
      }}
      onClose={onClose}
      onClick={(ev) => ev.stopPropagation()}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}>
      <Grid container style={{ fontSize: '14px' }}>
        <Grid item xs={12}>
          <PopupTitle
            titleText={titleText}
            closeButtonEnabled
            onClickClose={onClose}
          />
        </Grid>
        {copyToProgrammesEnabled && (
          <Grid item xs={12} style={{ padding: '10px 5px 5px 5px' }}>
            <Select
              variant="standard"
              value={destination}
              label="Destination"
              onChange={(ev) => {
                const newDestination = ev.target.value;
                onChangeDestination(newDestination);
              }}>
              {copyToPlannerEnabled && (
                <MenuItem value={PLANNER_DESTINATION}>My planner</MenuItem>
              )}

              {programmes.map((pr) => (
                <MenuItem key={pr.id} value={pr.id}>
                  {pr.title}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}
        {willCopyToPlanner && (
          <>
            <Grid item xs={12} style={{ padding: '10px 5px 5px 5px' }}>
              Select week:
            </Grid>
            <Grid item xs={12}>
              <WeekNavigator
                startDate={plannerViewWeekStartDate}
                onChangeStartDate={setPlannerViewWeekStartDate}
                innerWeekWidth={140}
              />
            </Grid>
          </>
        )}
        {!willCopyToPlanner && destinationProgramme && (
          <>
            <Grid item xs={12} style={{ padding: '10px 5px 5px 5px' }}>
              Select plan:
            </Grid>
            <Grid item xs={12}>
              <PlanNavigator
                currentPlanId={destinationPlanId}
                plans={destinationProgramme.plans}
                onChangePlan={onChangeDestinationPlanId}
              />
            </Grid>
          </>
        )}
        {!weekDaySelectionDisabled && (
          <>
            <Grid item xs={12} style={{ padding: '10px 5px 5px 5px' }}>
              Select days:
            </Grid>
            <Grid item xs={12} style={{ padding: '0 5px 5px 5px' }}>
              <Grid container>
                {allDayIndexes.map((i) => (
                  <Grid
                    key={i}
                    item
                    style={{
                      fontSize: '14px',
                      width: 40,
                      height: 40,
                      display: 'flex',
                    }}>
                    {dayIndexes.includes(i) ? (
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          width: 40,
                          height: 40,
                          fontSize: '14px',
                          cursor: 'pointer',
                        }}
                        onClick={(ev) => {
                          onExcludeDayIndex(i);
                          ev.stopPropagation();
                        }}>
                        {DAY_INDEX_SHORT_LABELS[i]}
                      </Avatar>
                    ) : (
                      // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                      <div
                        style={{
                          color: theme.palette.primary.main,
                          margin: 'auto',
                          cursor: 'pointer',
                        }}
                        onClick={(ev) => {
                          onIncludeDayIndex(i);
                          ev.stopPropagation();
                        }}>
                        {DAY_INDEX_SHORT_LABELS[i]}
                      </div>
                    )}
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </>
        )}
        {willCopyToPlanner && editServingsEnabled && (
          <>
            <Grid item xs={12} style={{ padding: '0 5px', display: 'flex' }}>
              <div style={{ margin: 'auto 0' }}>How many servings:</div>
            </Grid>
            <Grid item xs={6} style={{ padding: '5px', display: 'flex' }}>
              <div style={{ margin: 'auto 0' }}>Adults:</div>
            </Grid>
            <Grid item xs={6} style={{ padding: '5px', display: 'flex' }}>
              <div
                className="mealCardServingsStepInput"
                style={{ margin: 'auto' }}>
                <StepInput
                  value={scaleMealsToStrs.adults}
                  onChange={(ev) =>
                    onChangeScaleMealsTo('adults', ev.target.value)
                  }
                />
              </div>
            </Grid>
            <Grid item xs={6} style={{ padding: '5px', display: 'flex' }}>
              <div style={{ margin: 'auto 0' }}>Children:</div>
            </Grid>
            <Grid item xs={6} style={{ padding: '5px', display: 'flex' }}>
              <div
                className="mealCardServingsStepInput"
                style={{ margin: 'auto' }}>
                <StepInput
                  value={scaleMealsToStrs.children}
                  onChange={(ev) =>
                    onChangeScaleMealsTo('children', ev.target.value)
                  }
                />
              </div>
            </Grid>
          </>
        )}
        <Grid item xs={12} style={{ padding: '15px 5px 5px' }}>
          <Button
            onClick={(ev) => {
              dispatch(switchToViewWeek(plannerViewWeekStartDate));
              if (willCopyToPlanner) {
                onCopyToPlanner(
                  plannerViewWeekStartDate,
                  dayIndexes,
                  editServingsEnabled
                    ? aggregateServings(scaleMealsTo)
                    : undefined,
                );
              } else {
                onCopyToProgramme(destination, destinationPlanId, dayIndexes);
              }
              ev.stopPropagation();
            }}
            variant="contained"
            size="medium"
            color="primary"
            disableElevation
            disabled={!weekDaySelectionDisabled && dayIndexes.length === 0}
            style={{ justifyContent: 'flex-start' }}>
            Copy
          </Button>
        </Grid>
      </Grid>
    </Popover>
  );
};

AddToPlannerModal.propTypes = {
  anchorEl: PropTypes.element.isRequired,
  copyToPlannerEnabled: PropTypes.bool,
  copyToProgrammesEnabled: PropTypes.bool,
  onCopyToPlanner: PropTypes.func.isRequired,
  onCopyToProgramme: PropTypes.func,
  weekDaySelectionDisabled: PropTypes.bool,
  destinationProgrammeIDs: PropTypes.arrayOf(PropTypes.string),
  onDismiss: PropTypes.func.isRequired,
  titleText: PropTypes.string,
  editServingsEnabled: PropTypes.bool,
};

AddToPlannerModal.defaultProps = {
  copyToPlannerEnabled: false,
  copyToProgrammesEnabled: false,
  onCopyToProgramme: () => {},
  weekDaySelectionDisabled: false,
  destinationProgrammeIDs: [],
  titleText: 'Copy to plan',
  editServingsEnabled: false,
};

export default AddToPlannerModal;
